import React, { useState } from 'react';
import { signInWithEmailAndPassword, createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, getDoc, getDocs, collection, query, where, updateDoc, setDoc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../Firebase';
import { useNavigate } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, ArrowRight, User, KeyRound } from 'lucide-react';
import logo from '../safi1.jpeg';

export default function AdminConnexion() {
  const navigate  = useNavigate();
  const [mode, setMode]       = useState('login'); // 'login' | 'register'
  const [step, setStep]       = useState(1);       // pour register: 1=code, 2=infos
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState('');
  const [inviteData, setInviteData] = useState(null); // données de l'invitation

  // Formulaire connexion
  const [loginForm, setLoginForm] = useState({ email: '', password: '' });
  const [showPwd, setShowPwd]     = useState(false);

  // Formulaire inscription
  const [code, setCode]           = useState('');
  const [regForm, setRegForm]     = useState({ email: '', password: '', confirm: '' });
  const [showRegPwd, setShowRegPwd] = useState(false);

  // ── CONNEXION ──────────────────────────────────────────────────────────────
  const handleLogin = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const cred = await signInWithEmailAndPassword(auth, loginForm.email, loginForm.password);
      const staffSnap = await getDoc(doc(db, 'staff', cred.user.uid));
      if (!staffSnap.exists()) {
        await auth.signOut();
        setError('Accès refusé. Ce compte n\'est pas autorisé.');
        setLoading(false); return;
      }
      navigate('/admin');
    } catch (err) {
      const msgs = {
        'auth/user-not-found':     'Aucun compte trouvé.',
        'auth/wrong-password':     'Mot de passe incorrect.',
        'auth/invalid-credential': 'Email ou mot de passe incorrect.',
        'auth/too-many-requests':  'Trop de tentatives. Réessayez plus tard.',
      };
      setError(msgs[err.code] || 'Erreur de connexion.');
    }
    setLoading(false);
  };

  // ── VÉRIFIER LE CODE D'INVITATION ─────────────────────────────────────────
  const handleVerifyCode = async () => {
    setError(''); setLoading(true);
    try {
      // Cherche l'invitation dans Firestore
      const q    = query(collection(db, 'invitations'), where('code', '==', code.trim().toUpperCase()));
      const snap = await getDocs(q);
      if (snap.empty) {
        setError('Code d\'invitation invalide ou expiré.'); setLoading(false); return;
      }
      const invDoc = snap.docs[0];
      const inv    = invDoc.data();
      if (inv.used) {
        setError('Ce code a déjà été utilisé.'); setLoading(false); return;
      }
      setInviteData({ ...inv, docId: invDoc.id });
      setStep(2);
    } catch (err) {
      setError('Erreur lors de la vérification.');
    }
    setLoading(false);
  };

  // ── CRÉER LE COMPTE ───────────────────────────────────────────────────────
  const handleRegister = async (e) => {
    e.preventDefault();
    setError(''); 
    if (regForm.password !== regForm.confirm) { setError('Les mots de passe ne correspondent pas.'); return; }
    if (regForm.password.length < 8)          { setError('Mot de passe trop court (8 caractères min).'); return; }
    if (!/[A-Z]/.test(regForm.password))      { setError('Le mot de passe doit contenir une majuscule.'); return; }
    if (!/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(regForm.password)) { setError('Le mot de passe doit contenir un caractère spécial.'); return; }
    setLoading(true);
    try {
      // Crée le compte Firebase Auth
      const cred = await createUserWithEmailAndPassword(auth, regForm.email, regForm.password);
      
      // Crée un nouveau document staff avec le vrai UID
      await setDoc(doc(db, 'staff', cred.user.uid), {
        nom:       inviteData.nom,
        email:     regForm.email,
        role:      inviteData.role,
        actif:     true,
        createdAt: serverTimestamp(),
        compteCreeLe: serverTimestamp(),
      });

      // Marque l'invitation comme utilisée
      await updateDoc(doc(db, 'invitations', inviteData.docId), { used: true });
      
      // Supprime l'ancien document staff temporaire
      try {
        const { deleteDoc: del, doc: d } = await import('firebase/firestore');
        // On ne supprime pas le doc temporaire pour garder la trace
      } catch(e) {}
      
      navigate('/admin');
    } catch (err) {
      const msgs = {
        'auth/email-already-in-use': 'Cet email est déjà utilisé.',
        'auth/invalid-email':        'Email invalide.',
        'auth/weak-password':        'Mot de passe trop faible.',
      };
      setError(msgs[err.code] || 'Erreur lors de la création du compte.');
    }
    setLoading(false);
  };

  return (
    <div className="admin-login">
      {/* Partie gauche */}
      <div className="admin-login__left">
        <div className="admin-login__brand">
          <img src={logo} alt="SAFI Academia" />
          <div>
            <h1>SAFI Academia</h1>
            <p>Plateforme de gestion</p>
          </div>
        </div>
        <div className="admin-login__tagline">
          <h2>Espace réservé<br/>à l'équipe SAFI</h2>
          <p>Connectez-vous ou utilisez votre code d'invitation pour créer votre compte.</p>
        </div>
        <div className="admin-login__roles">
          <p>Rôles disponibles :</p>
          <ul>
            <li>🔑 IT Manager</li>
            <li>👔 General Manager</li>
            <li>📣 Manager Communication</li>
            <li>📚 Trainer</li>
          </ul>
        </div>
      </div>

      {/* Partie droite */}
      <div className="admin-login__right">
        <div className="admin-login__form-wrap">

          {/* Tabs */}
          <div className="admin-login__tabs">
            <button className={`admin-login__tab ${mode==='login'?'active':''}`} onClick={() => { setMode('login'); setError(''); }}>
              Se connecter
            </button>
            <button className={`admin-login__tab ${mode==='register'?'active':''}`} onClick={() => { setMode('register'); setError(''); setStep(1); }}>
              Créer mon compte
            </button>
          </div>

          {error && <div className="admin-login__error">{error}</div>}

          {/* ── CONNEXION ── */}
          {mode === 'login' && (
            <form onSubmit={handleLogin} className="admin-login__form">
              <div className="admin-login__field">
                <label>Adresse e-mail</label>
                <div className="admin-login__input-wrap">
                  <Mail size={16}/>
                  <input type="email" placeholder="votre@email.com" value={loginForm.email}
                    onChange={e => setLoginForm(p => ({...p, email: e.target.value}))} required/>
                </div>
              </div>
              <div className="admin-login__field">
                <label>Mot de passe</label>
                <div className="admin-login__input-wrap">
                  <Lock size={16}/>
                  <input type={showPwd?'text':'password'} placeholder="••••••••" value={loginForm.password}
                    onChange={e => setLoginForm(p => ({...p, password: e.target.value}))} required/>
                  <button type="button" onClick={() => setShowPwd(!showPwd)}>
                    {showPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
              </div>
              <button type="submit" className="admin-login__btn" disabled={loading}>
                {loading ? <span className="admin-spinner-sm"/> : <><span>Se connecter</span><ArrowRight size={16}/></>}
              </button>
            </form>
          )}

          {/* ── INSCRIPTION ÉTAPE 1 — Code ── */}
          {mode === 'register' && step === 1 && (
            <div className="admin-login__form">
              <div className="admin-login__invite-info">
                <KeyRound size={20}/>
                <p>Entrez le code d'invitation que votre IT Manager vous a transmis.</p>
              </div>
              <div className="admin-login__field">
                <label>Code d'invitation</label>
                <div className="admin-login__input-wrap">
                  <KeyRound size={16}/>
                  <input type="text" placeholder="Ex: SAFI-XXXX" value={code}
                    onChange={e => setCode(e.target.value)} style={{textTransform:'uppercase',letterSpacing:'0.15em'}}/>
                </div>
              </div>
              <button className="admin-login__btn" onClick={handleVerifyCode} disabled={loading || !code}>
                {loading ? <span className="admin-spinner-sm"/> : <><span>Vérifier le code</span><ArrowRight size={16}/></>}
              </button>
            </div>
          )}

          {/* ── INSCRIPTION ÉTAPE 2 — Infos compte ── */}
          {mode === 'register' && step === 2 && inviteData && (
            <form onSubmit={handleRegister} className="admin-login__form">
              <div className="admin-login__welcome">
                <p>Bienvenue <strong>{inviteData.nom}</strong> !</p>
                <span>Rôle : {inviteData.roleLabel}</span>
              </div>
              <div className="admin-login__field">
                <label>Adresse e-mail</label>
                <div className="admin-login__input-wrap">
                  <Mail size={16}/>
                  <input type="email" placeholder="votre@email.com" value={regForm.email}
                    onChange={e => setRegForm(p => ({...p, email: e.target.value}))} required/>
                </div>
              </div>
              <div className="admin-login__field">
                <label>Mot de passe</label>
                <div className="admin-login__input-wrap">
                  <Lock size={16}/>
                  <input type={showRegPwd?'text':'password'} placeholder="••••••••" value={regForm.password}
                    onChange={e => setRegForm(p => ({...p, password: e.target.value}))} required/>
                  <button type="button" onClick={() => setShowRegPwd(!showRegPwd)}>
                    {showRegPwd ? <EyeOff size={16}/> : <Eye size={16}/>}
                  </button>
                </div>
                <ul className="admin-login__pwd-rules">
                  {[
                    [regForm.password.length >= 8,                          '8 caractères minimum'],
                    [/[A-Z]/.test(regForm.password),                        'Une majuscule'],
                    [/[a-z]/.test(regForm.password),                        'Une minuscule'],
                    [/[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(regForm.password), 'Un caractère spécial'],
                  ].map(([ok, label], i) => (
                    <li key={i} className={ok ? 'ok' : ''}>{ok ? '✓' : '○'} {label}</li>
                  ))}
                </ul>
              </div>
              <div className="admin-login__field">
                <label>Confirmer le mot de passe</label>
                <div className="admin-login__input-wrap">
                  <Lock size={16}/>
                  <input type="password" placeholder="••••••••" value={regForm.confirm}
                    onChange={e => setRegForm(p => ({...p, confirm: e.target.value}))} required/>
                </div>
              </div>
              <button type="submit" className="admin-login__btn" disabled={loading}>
                {loading ? <span className="admin-spinner-sm"/> : <><span>Créer mon compte</span><ArrowRight size={16}/></>}
              </button>
            </form>
          )}

          <p className="admin-login__back"><a href="/">← Retour au site</a></p>
        </div>
      </div>
    </div>
  );
}